import React from 'react';
import PropTypes from 'prop-types';
import { Button, Popconfirm } from 'antd';
import NavigationOption from '~pages/dashboard/properties/listing-management/navigation-bar/option';
import UploadProgress from '~pages/dashboard/properties/listing-management/upload-progress';
import {
  propertySections,
  noAcPropertySection,
  sectionPropertyState,
  propertyStatus,
} from '~constants/listing-management';
import { communicationStatus } from '~client/constants';
import { Preview as PreviewIcon } from 'common/components/svgs';
import { isAdminRole, onlyViewPropertyRole } from '~helpers/auth';
import { authUrl } from '~settings/endpoints';
import ListingNoAvailable, {
  isInVentoryCountsEmpty,
} from '~pages/dashboard/properties/listing-management/listingNoAvailable';
import { bookingJourneyType } from 'common/constants/booking';
import PropertyConfirmUnpublishModal from '~pages/dashboard/properties/bulk-operation/confirm-modal';

export function showNavigationLabels(property) {
  let navigationLabArr = null;

  if (
    (property?.bookingJourney === bookingJourneyType.SEMI_AUTOMATIC ||
      property?.bookingJourney === bookingJourneyType.INSTANT_BOOK) &&
    !onlyViewPropertyRole()
  ) {
    navigationLabArr = new Set([...Object.keys(propertySections)]);
  } else {
    navigationLabArr = new Set([...Object.keys(noAcPropertySection)]);
  }

  if (
    property?.bookingJourney === bookingJourneyType.LEAD_GENERATION ||
    property?.bookingJourney === bookingJourneyType.FULL_AUTOMATIC
  ) {
    navigationLabArr.delete(propertySections.PAYMENT_PLAN);
  }

  if (isAdminRole()) {
    navigationLabArr?.add('TAG');
  } else {
    navigationLabArr?.delete('TAG');
  }

  // Filter out price calc display rule.
  if (
    property?.billingCycle !== 'MONTHLY' ||
    property?.bookingJourney === bookingJourneyType.FULL_AUTOMATIC
  ) {
    navigationLabArr.delete(propertySections.PRICE_CALC_DISPLAY);
  }

  return [...navigationLabArr];
}

export default class NavigationBar extends React.Component {
  constructor() {
    super();
    this.state = {
      isShowUnpublishModal: false,
      isShowListingEmptyModal: false,
    };
  }

  calSectionState = section => {
    const { property } = this.props;
    const {
      name,
      slug,
      unitTypes,
      allImages,
      facilities,
      shippingCity,
      country,
      address,
      paymentPlan,
      profile,
    } = property;

    const { FILLED, NOT_FILLED, NOT_SUFFICIENT } = sectionPropertyState;

    switch (section) {
      case propertySections.PROPERTY_DETAILS:
        if (name && slug) {
          return FILLED;
        } else {
          return NOT_FILLED;
        }
      case propertySections.PROPERTY_ADDRESS:
        if (shippingCity && country && address) {
          return FILLED;
        } else {
          return NOT_FILLED;
        }
      case propertySections.PROPERTY_FACILITY:
        if (facilities.some(facility => facility.group !== 'others' && facility.checked)) {
          return FILLED;
        } else {
          return NOT_SUFFICIENT;
        }
      case propertySections.ROOM_CONFIG:
        if (unitTypes && unitTypes.edges.length > 0) {
          return FILLED;
        } else {
          return NOT_SUFFICIENT;
        }
      case propertySections.PAYMENT_PLAN:
        if (paymentPlan) {
          return FILLED;
        } else {
          return NOT_SUFFICIENT;
        }
      case propertySections.PRICE_AND_AVAILABILITY:
        if (
          unitTypes?.edges?.some(unit => unit.node && unit.node.listings.length > 0) &&
          !this.props.isHaveUnconfirmedListing
        ) {
          return FILLED;
        } else {
          return NOT_SUFFICIENT;
        }
      case propertySections.ACCEPTANCE_CRITERIA:
        return FILLED;
      case propertySections.TAG:
        return FILLED;
      case propertySections.SEO_SETTING:
        if (!profile.metaTitleEnabled && !profile.metaTitle) {
          return NOT_SUFFICIENT;
        }
        if (!profile.metaDescriptionEnabled && !profile.metaDescription) {
          return NOT_SUFFICIENT;
        }
        return FILLED;
      case propertySections.PRICE_CALC_DISPLAY:
        return FILLED;
      case propertySections.GALLERY_MANAGEMENT:
        if (
          allImages.edges?.some(
            image =>
              image.node &&
              image.node.hero &&
              image.node.status !== 'REJECTED' &&
              !image.node.deleted,
          )
        ) {
          return FILLED;
        } else {
          return NOT_FILLED;
        }
      default:
        return '';
    }
  };

  showUnpublishModal = () => {
    this.setState({
      isShowUnpublishModal: true,
    });
  };

  handleCancel = () => {
    this.setState({
      isShowUnpublishModal: false,
    });
  };

  getCityCountry = () => {
    const { property } = this.props;
    const cityArr = [];
    if (property.city) {
      cityArr.push(property.city.name);

      if (property.city.country) {
        cityArr.push(property.city.country.name);
      }
    }

    return cityArr.join();
  };

  handleClickPreivew = () => {
    this.props.handlePreview(res => {
      if (res && res.slug && res.city.slug && res.city.country && res.city.country.slug) {
        this.previewForm.action = authUrl(
          `${res.city.country.slug}/${res.city.slug}/p/${res.slug}?preview=yes&update_cache=yes`,
        );
        document.getElementById('preview_input').value = JSON.stringify({
          property: res,
        });
        this.previewForm.submit();
      }
      return false;
    });
  };

  handleClickUnpublish = unpublishReason => {
    this.props.handlePublish({ isPublish: false, unpublishReason: unpublishReason }, () => {
      this.setState({
        isShowUnpublishModal: false,
      });
    });
  };

  calIsDisabled = () => {
    if (onlyViewPropertyRole()) {
      return true;
    }
    const { name, slug, shippingCity, country, status, publishCondition, allImages } =
      this.props.property;
    const { commissionTier, contractStage, paymentPlan, payout, listing, offlinePaymentMethod } =
      publishCondition;

    const payoutOrOfflinePaymentMethod = this.props.property?.paymentPlan?.paymentOnline
      ? payout
      : offlinePaymentMethod;

    if (
      name &&
      slug &&
      shippingCity &&
      country &&
      status !== propertyStatus.NEW &&
      allImages.edges &&
      allImages.edges.some(
        image =>
          image.node && image.node.hero && image.node.status !== 'REJECTED' && !image.node.deleted,
      ) &&
      commissionTier &&
      contractStage &&
      paymentPlan &&
      payoutOrOfflinePaymentMethod &&
      listing
    ) {
      return false;
    }

    return true;
  };

  handleConfirmPublish = () => {
    this.props.onClearUnconfirmedListings();
    this.props.handlePublish({ isPublish: true });
  };
  publishHandle = hasInVentory => {
    const { isShowListingEmptyModal } = this.state;
    // listing available 为0 时，显示出 pop-up
    if (hasInVentory) {
      if (!isShowListingEmptyModal) {
        this.setState({ isShowListingEmptyModal: true });
        return;
      }
    }
    this.props.handlePublish({ isPublish: true });
  };
  componentWillReceiveProps(nextProps) {
    if (!nextProps.isHaveUnconfirmedListing) {
      this.setState({ isShowListingEmptyModal: false });
    }
  }

  render() {
    const { t, currentSection, property, publishStatus, isHaveUnconfirmedListing } = this.props;
    const { isShowListingEmptyModal } = this.state;
    const hasInVentory = isInVentoryCountsEmpty(property?.unitTypes?.edges);
    const availableNavigationLabels = showNavigationLabels(property);
    return (
      <div className="navigation-bar">
        <div className="navigation-bar__selection-wrap">
          <ul className="navigation-bar__selection">
            <For each="section" of={availableNavigationLabels} index="index">
              <NavigationOption
                key={index}
                sectionState={this.calSectionState(section)}
                text={t(`cms.property.listing_management.${section.toLowerCase()}.navigation`)}
                isSelected={currentSection === section}
                handleSelectOption={() => this.props.handleSetSection(section)}
              />
            </For>
          </ul>
        </div>
        <UploadProgress t={t} history={this.props.history} />
        <Choose>
          <When condition={property.status === propertyStatus.PUBLISHED}>
            <div className="navigation-bar__unpublished-wrap">
              <span role="presentation" onClick={this.showUnpublishModal}>
                {t('cms.property.listing_management.unpublish.button')}
              </span>
            </div>
            <If condition={this.state.isShowUnpublishModal}>
              <PropertyConfirmUnpublishModal
                activeModal
                t={t}
                selectedProperties={[property.id]}
                list={[
                  {
                    ...property,
                    landlordName: property.landlord ? property.landlord.name : '',
                    cityName: this.getCityCountry(),
                  },
                ]}
                onSuccess={this.handleClickUnpublish}
                handleCloseModal={this.handleCancel}
              />
            </If>
          </When>
          <Otherwise>
            <Choose>
              <When
                condition={
                  property?.bookingJourney === bookingJourneyType.INSTANT_BOOK &&
                  this.calIsDisabled()
                }
              >
                <Button
                  block
                  type="primary"
                  size="large"
                  disabled
                  loading={publishStatus === communicationStatus.FETCHING}
                  className="navigation-bar__publish-btn"
                >
                  {t('cms.property.listing_management.publish.button')}
                </Button>
              </When>
              <When condition={isHaveUnconfirmedListing}>
                <Popconfirm
                  placement="top"
                  overlayStyle={{ maxWidth: 350 }}
                  title={t('cms.property.listing_management.unconfirmed_lisitings.publish.tips')}
                  onConfirm={this.handleConfirmPublish}
                  okText={t('cms.properties.edit.btn.yes')}
                  cancelText={t('cms.properties.edit.btn.no')}
                >
                  <Button
                    block
                    type="primary"
                    size="large"
                    disabled={this.calIsDisabled()}
                    loading={publishStatus === communicationStatus.FETCHING}
                    className="navigation-bar__publish-btn"
                  >
                    {t('cms.property.listing_management.publish.button')}
                  </Button>
                </Popconfirm>
              </When>
              <Otherwise>
                <ListingNoAvailable
                  confirmHandle={() => {
                    this.props.handlePublish({ isPublish: true });
                  }}
                  NewClass={'navigation-bar__publish-popUp'}
                  isShow={isShowListingEmptyModal}
                  onCancel={() => {
                    this.setState({ isShowListingEmptyModal: false });
                  }}
                />
                <Button
                  block
                  type="primary"
                  size="large"
                  disabled={this.calIsDisabled() || isShowListingEmptyModal}
                  loading={publishStatus === communicationStatus.FETCHING}
                  className="navigation-bar__publish-btn"
                  onClick={() => this.publishHandle(hasInVentory)}
                >
                  {t('cms.property.listing_management.publish.button')}
                </Button>
              </Otherwise>
            </Choose>
            <form
              method="post"
              target="_blank"
              ref={node => {
                this.previewForm = node;
              }}
            >
              <input type="hidden" id="preview_input" name="data" value="" />
              <Button block ghost type="primary" size="large" onClick={this.handleClickPreivew}>
                <PreviewIcon className="navigation-bar__preview-icon" />
                {t('cms.property.listing_management.preview.button')}
              </Button>
            </form>
          </Otherwise>
        </Choose>
      </div>
    );
  }
}

NavigationBar.propTypes = {
  t: PropTypes.func.isRequired,
  property: PropTypes.object,
  currentSection: PropTypes.string,
  handleSetSection: PropTypes.func,
  publishStatus: PropTypes.string,
  handlePreview: PropTypes.func.isRequired,
  handlePublish: PropTypes.func.isRequired,
  isHaveUnconfirmedListing: PropTypes.bool,
  history: PropTypes.object,
  onClearUnconfirmedListings: PropTypes.func,
};

NavigationBar.defaultProps = {
  t: () => {},
  property: {},
  currentSection: '',
  handleSetSection: () => {},
  publishStatus: '',
  handlePreview: () => {},
  handlePublish: () => {},
  isHaveUnconfirmedListing: false,
  history: {},
  onClearUnconfirmedListings: () => {},
};
